import { Children, FC, SyntheticEvent, useEffect, useMemo, useRef } from 'react';
import { TabbedShowLayoutTabs, TabbedShowLayoutTabsProps } from 'react-admin';
import { useSearchParams } from 'react-router-dom';

export const TabbedShowLayoutSearchTabs: FC<
  { searchParam?: string } & TabbedShowLayoutTabsProps
> = ({ searchParam = 'tab', onChange, ...props }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTabKey = searchParams.get(searchParam);
  // react-admin's own list params syncing (page/sort/filter changes) replaces the
  // whole query string, dropping unrelated params like `tab`. Fall back to the last
  // known tab so we don't flash back to the first tab while we restore it below.
  const lastTabKeyRef = useRef<string | null>(null);
  const tabKey = rawTabKey ?? lastTabKeyRef.current;

  useEffect(() => {
    if (rawTabKey) {
      lastTabKeyRef.current = rawTabKey;
      return;
    }
    if (lastTabKeyRef.current) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(searchParam, lastTabKeyRef.current as string);
          return next;
        },
        { replace: true },
      );
    }
  }, [rawTabKey, searchParam, setSearchParams]);
  const tabKeys = useMemo(
    () =>
      Children.map(props.children, (child, index) => ({
        path: child?.props.path ?? index,
        syncWithLocation: child?.props.syncWithLocation,
      })) ?? [],
    [props.children],
  );
  const currentIndex = useMemo(() => {
    if (!tabKey) {
      return 0;
    }
    const idx = tabKeys.findIndex((entry) => entry.path === tabKey);
    if (idx === -1) {
      return 0;
    }
    return idx;
  }, [tabKey, tabKeys]);

  useEffect(() => {
    if (onChange) {
      onChange({} as unknown as SyntheticEvent, currentIndex);
    }
  }, [currentIndex, onChange]);

  return (
    <nav aria-label="submenu">
      <TabbedShowLayoutTabs
        {...props}
        value={currentIndex}
        onChange={(_event: SyntheticEvent, value: number) => {
          if (tabKeys[value].syncWithLocation !== false) {
            // Use replace mode to avoid adding history entries for tab changes
            // This allows the back button to navigate to the previous resource
            // instead of the previous tab
            setSearchParams({ [searchParam]: tabKeys[value].path.toString() }, { replace: true });
          }
        }}
      />
    </nav>
  );
};
